import 'video.js/dist/video-js.css';
import '@videojs/themes/dist/city/index.css';

import cn from 'classnames';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import Player from 'video.js/dist/types/player';

import { setupCustomControls } from './hooks/useCustomControls';
import styles from './styles.module.scss';
import { hideControlBar, initializePlayer, setupPlayerEvents, showControlBar } from './utils/playerUtils';

export interface VideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  src?: string;
  poster?: string;
  controls?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  width?: number | string;
  height?: number | string;
  preload?: 'none' | 'metadata' | 'auto';
  children?: React.ReactNode;
}

const NextVideo: React.FC<VideoProps> = ({
  src,
  poster,
  controls = true,
  className,
  children,
  autoPlay = false,
  loop = false,
  muted = false,
  playsInline = false,
  preload = 'metadata',
  width,
  height,
  ...props
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<Player | null>(null);
  const hasStartedPlayingRef = useRef(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(true);

  useEffect(() => {
    if (!videoRef.current) return;

    const player = initializePlayer(videoRef.current, {
      controls: false, // Start with controls hidden
      autoplay: autoPlay,
      preload,
      loop,
      muted,
      playsinline: playsInline,
      poster,
      fluid: !width && !height,
      responsive: true,
      bigPlayButton: true,
      disablePictureInPicture: true
    });

    playerRef.current = player;

    player.ready(() => {
      setupCustomControls(player);

      const bigPlayButton = player.getChild('BigPlayButton');
      if (bigPlayButton) {
        bigPlayButton.show();
      }
    });

    if (src) {
      player.src({ src, type: 'video/mp4' });
    }

    player.addClass('vjs-theme-city');

    const controlBar = hideControlBar(player);

    setupPlayerEvents(
      player,
      () => {
        hasStartedPlayingRef.current = true;
        setHasStartedPlaying(true);
        setIsPaused(false);
        showControlBar(controlBar);
        if (controls) {
          player.controls(true);
        }
        const btn = player.getChild('BigPlayButton');
        if (btn) {
          btn.hide();
        }
      },
      () => {
        if (hasStartedPlayingRef.current && controlBar) {
          showControlBar(controlBar);
        }
      }
    );

    player.on('pause', () => {
      setIsPaused(true);
      // Show big play button when paused
      const btn = player.getChild('BigPlayButton');
      if (btn) {
        btn.show();
      }
    });

    player.on('play', () => {
      setIsPaused(false);
      // Hide big play button when playing
      const btn = player.getChild('BigPlayButton');
      if (btn) {
        btn.hide();
      }
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [src, poster, controls, autoPlay, loop, muted, playsInline, preload, width, height]);

  return (
    <div data-vjs-player className={cn(styles.videoContainer, className)}>
      {(!hasStartedPlaying || isPaused) && <div className={styles.backdrop} />}
      {
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          ref={videoRef}
          className={cn('video-js', styles.video)}
          style={{ width: width || '100%', height: height || 'auto' }}
          {...props}
        >
          {children}
        </video>
      }
    </div>
  );
};

export default NextVideo;
